import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

const to = new Date();
// Set a more reasonable default date range - last 12 months instead of from 2000
const from = new Date(to);
from.setFullYear(from.getFullYear() - 1);

// Your existing types (keep them as they are)
export type AuthenticationType = Record<string, unknown>;
export type KYCType = unknown;
export type HubspotType = Record<string, unknown>;

export type OnboardingType = {
  howHear: string | null;
  role: string | null;
  accountName: string | null;
  address1: string | null;
  address2: string | null;
  town: string | null;
  county: string | null;
  postcode: string | null;
  country: string | null;
};

export type AddAccountType = {
  name: string | null;
  cataloguecode?: string | null;
  live: boolean | null;
  streaming: boolean | null;
  spotify: string | null;
  facebook: string | null;
  twitter: string | null;
};

export type SongsTableType = {
  page: number;
  perPage: number;
  sortBy: Array<{ id: string; desc: boolean }> | null; // Changed to match original
  globalFilter: string | null;
};

export type DateRangeFormat = {
  format: string;
  gte: string;
  lte: string;
};

export type RangeType = {
  receiptDate: DateRangeFormat;
  publishedStatementDate: DateRangeFormat;
};

export type SelectedDatesType = {
  fromDate: string;
  toDate: string;
};

export type DataRangeType = {
  from: Date | null;
  to: Date | null;
};

export type SiteConfigType = {
  display_name?: string;
  spotify_api_url?: string;
  [key: string]: unknown;
};

// Simplified state type - just the data, actions separate
type AppStateData = {
  authentication: AuthenticationType;
  kyc: KYCType;
  hubspot: HubspotType;
  selectedAccount: unknown;
  allSyncsAvailable: unknown[] | null;
  allOpportunitiesAvailable: unknown[] | null;
  selectedCatalogue: unknown;
  tenantCurrencyCode: string;
  tenantCurrencySymbol: string;
  tenantCurrencyLocale: string;
  serverError: Error | null;
  onboarding: OnboardingType;
  addAccount: AddAccountType;
  spotifyAccountMethod: string;
  agreedTerms: boolean;
  songsTable: SongsTableType;
  range: RangeType;
  selectedDates: SelectedDatesType;
  dataRange: DataRangeType;
  royaltiesFilter: unknown[];
  navItemsEnabled: boolean;
  apiUrl: string;
  hubspotDisabled: boolean | null;
  clientDisplayName: string;
  siteConfig?: SiteConfigType;
};

// Actions type
type AppStateActions = {
  updateAddAccount: (updates: Partial<AddAccountType>) => void;
  updateOnboarding: (updates: Partial<OnboardingType>) => void;
  updateSongsTable: (updates: Partial<SongsTableType>) => void;
  setSelectedAccount: (account: unknown) => void;
  setSelectedCatalogue: (catalogue: unknown) => void;
  setServerError: (error: Error | null) => void;
  setRoyaltiesFilter: (filters: unknown[]) => void;
};

// Combined type
export type AppState = AppStateData & AppStateActions;

// Initial state matching your original exactly
const initialState: AppStateData = {
  authentication: {},
  kyc: null,
  hubspot: {},
  selectedAccount: null,
  allSyncsAvailable: null,
  allOpportunitiesAvailable: null,
  selectedCatalogue: null,
  tenantCurrencyCode: "",
  tenantCurrencySymbol: "*",
  tenantCurrencyLocale: "",
  serverError: null,
  onboarding: {
    howHear: null,
    role: null,
    accountName: null,
    address1: null,
    address2: null,
    town: null,
    county: null,
    postcode: null,
    country: null,
  },
  addAccount: {
    name: null,
    cataloguecode: null,
    live: null,
    streaming: null,
    spotify: null,
    facebook: null,
    twitter: null,
  },
  spotifyAccountMethod: "",
  agreedTerms: false,
  songsTable: {
    page: 1,
    perPage: 10,
    sortBy: null, // Changed to match original
    globalFilter: null,
  },
  range: {
    receiptDate: {
      format: "strict_date_optional_time",
      gte: from.toISOString(),
      lte: to.toISOString(),
    },
    publishedStatementDate: {
      format: "strict_date_optional_time",
      gte: from.toISOString(),
      lte: to.toISOString(),
    },
  },
  selectedDates: {
    fromDate: from.toISOString(),
    toDate: to.toISOString(),
  },
  dataRange: {
    from: null,
    to: null,
  },
  royaltiesFilter: [],
  navItemsEnabled: true,
  apiUrl: import.meta.env.VITE_API as string,
  hubspotDisabled: null,
  clientDisplayName: "",
};

const useAppStore = create<AppState>()(
  persist(
    set => ({
      ...initialState,

      // Simple actions
      updateAddAccount: updates => set(state => ({
        addAccount: { ...state.addAccount, ...updates },
      })),

      updateOnboarding: updates => set(state => ({
        onboarding: { ...state.onboarding, ...updates },
      })),

      updateSongsTable: updates => set(state => ({
        songsTable: { ...state.songsTable, ...updates },
      })),

      setSelectedAccount: account => set({ selectedAccount: account }),
      setSelectedCatalogue: catalogue => set({ selectedCatalogue: catalogue }),
      setServerError: error => set({ serverError: error }),
      setRoyaltiesFilter: filters => set({ royaltiesFilter: filters }),
    }),
    {
      name: "@userData", // Use same key as original
      storage: createJSONStorage(() => sessionStorage),

      // Simplified merge function
      merge: (persistedState, currentState) => {
        if (typeof persistedState !== "object" || persistedState === null) {
          return currentState;
        }

        const state = { ...currentState, ...persistedState } as AppState;

        // Apply same migrations as original
        // eslint-disable-next-line ts/strict-boolean-expressions
        if (state.songsTable) {
          state.songsTable = {
            ...state.songsTable,
            sortBy: Array.isArray(state.songsTable.sortBy) ? state.songsTable.sortBy : [],
            globalFilter: state.songsTable.globalFilter ?? null,
          };
        }

        if (!Array.isArray(state.royaltiesFilter)) {
          state.royaltiesFilter = [];
        }

        if (state.navItemsEnabled === undefined) {
          state.navItemsEnabled = true;
        }

        return state;
      },
    },
  ),
);

export default useAppStore;
